import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ChevronDown, Download, Filter, RefreshCw } from "lucide-react";
import { useGRCData } from "@/services/grc";
import { useCanExportGRCReport } from "@/hooks/usePermissions";
import { useCanAddMeeting, useCanEditMeeting, usePermissions } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMsal } from "@azure/msal-react";
import { trackPageView, trackUserAction } from "@/lib/analytics";
import { useSoftHoldById } from "@/services/softHolds";
import { GRCGrid, GRCGridSkeleton } from "./components/GRCGrid";
import { MonthNav } from "./components/MonthNav";
import { MonthYearPicker } from "./components/MonthYearPicker";
import { GRCSearchBox } from "./components/GRCSearchBox";
import { GRCFilterPanel } from "./components/GRCFilterPanel";
import { GRCExportModal } from "./components/GRCExportModal";
import { SoftHoldsFlyout } from "./components/SoftHoldsFlyout";
import { CreateSoftHoldDialog } from "./components/CreateSoftHoldDialog";
import type { RoomBlockOption } from "./components/CreateSoftHoldDialog";
import type { RecommendationResponse } from "@/types/recommendation";
import { GRCBreadcrumbs } from "./components/GRCBreadcrumbs";
import type { SoftHoldBreadcrumb } from "./components/GRCBreadcrumbs";
import { BeaconWindow } from "@/features/recommendation/components/BeaconWindow";
import { featureFlags } from "@/config/env";
import { MeetingFlyout } from "@/features/draft-calendar/components/MeetingFlyout";
import {
  useFiscalYears,
  useDraftBusinesses,
  useDraftMeetingTypes,
  useAttendeeLevels,
  useLocations,
  useDUDeclinedReasons,
} from "@/services/draftCalendar";
import type { ProgramDetailDto, GRCActiveFilters, GRCSearchResult } from "@/types/grc";

const EMPTY_FILTERS: GRCActiveFilters = { eventTypes: [], statuses: [] };

function GRCDashboardContentSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-6 w-40 animate-pulse bg-slate-200" />
      <GRCGridSkeleton />
      <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="h-3 w-16 animate-pulse bg-slate-200" />
          <div className="h-3 w-16 animate-pulse bg-slate-200" />
          <div className="h-3 w-16 animate-pulse bg-slate-200" />
        </div>
        <div className="h-3 w-32 animate-pulse bg-slate-200" />
      </div>
    </div>
  );
}

export function GRCDashboardPage() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const currentUser = useCurrentUser();
  const userId = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("GRC Dashboard", userId);
  }, [userId]);

  const addMenuRef = useRef<HTMLDivElement>(null);
  const monthLabelRef = useRef<HTMLButtonElement>(null);
  const atlasButtonRef = useRef<HTMLButtonElement>(null);
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState(now);
  const [filters, setFilters] = useState<GRCActiveFilters>(EMPTY_FILTERS);
  const [showPickups, setShowPickups] = useState(false);
  const [showDatesAtBottom, setShowDatesAtBottom] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddNewMenu, setShowAddNewMenu] = useState(false);
  const [showCreateSoftHoldDialog, setShowCreateSoftHoldDialog] = useState(false);
  const [selectedSoftHoldDetailsId, setSelectedSoftHoldDetailsId] = useState<number | null>(null);
  const [softHoldInitialRoomBlocks, setSoftHoldInitialRoomBlocks] = useState<RoomBlockOption[] | undefined>(undefined);
  const [showSoftHoldsFlyout, setShowSoftHoldsFlyout] = useState(false);
  const [isSoftHoldsListOnlyMode, setIsSoftHoldsListOnlyMode] = useState(false);
  const [selectedSoftHoldId, setSelectedSoftHoldId] = useState(0);
  const [highlightName, setHighlightName] = useState<string | undefined>();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [showBeaconWindow, setShowBeaconWindow] = useState(false);
  // null = hidden, 0 = create new, >0 = view/edit existing
  const [draftFlyoutMeetingId, setDraftFlyoutMeetingId] = useState<number | null>(null);
  const [draftFlyoutViewOnly, setDraftFlyoutViewOnly] = useState(false);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<SoftHoldBreadcrumb[]>(() => {
    try {
      const stored = sessionStorage.getItem("grc_breadcrumbs");
      return stored ? (JSON.parse(stored) as SoftHoldBreadcrumb[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    sessionStorage.setItem("grc_breadcrumbs", JSON.stringify(breadcrumbs));
  }, [breadcrumbs]);

  const canExport = useCanExportGRCReport();
  // Auto-add breadcrumb when a soft hold with preferred dates is opened
  const { data: openedSoftHold } = useSoftHoldById(
    selectedSoftHoldDetailsId ?? 0,
    selectedSoftHoldDetailsId !== null && showCreateSoftHoldDialog
  );

  useEffect(() => {
    if (!openedSoftHold || !openedSoftHold.preferredDates?.length) return;
    setBreadcrumbs((prev) => {
      if (prev.some((b) => b.id === openedSoftHold.id)) return prev;
      const updated = [
        ...prev,
        {
          id: openedSoftHold.id,
          meetingId: openedSoftHold.meetingId ?? "",
          meetingName: openedSoftHold.meetingName,
          preferredDates: openedSoftHold.preferredDates.map((d) => ({
            startDate: d.startDate,
            endDate: d.endDate,
            isItPrimaryDate: d.isItPrimaryDate
          }))
        }
      ];
      // Keep only the 5 most recent
      return updated.slice(-5);
    });
  }, [openedSoftHold]);

  function handleBreadcrumbNavigate(breadcrumb: SoftHoldBreadcrumb) {
    const primaryDate =
      breadcrumb.preferredDates.find((d) => d.isItPrimaryDate) ?? breadcrumb.preferredDates[0];
    if (primaryDate?.startDate) {
      const target = new Date(primaryDate.startDate);
      setSelectedDate(new Date(target.getFullYear(), target.getMonth(), 1));
    }
    setIsSoftHoldsListOnlyMode(false);
    setSelectedSoftHoldDetailsId(breadcrumb.id);
    setShowCreateSoftHoldDialog(true);
  }

  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const { hasPermission } = usePermissions();
  const canSubmit = hasPermission("CanSubmitToApprover");
  const canViewToggleSettings = hasPermission("CanViewToggleSettings");
  const userRoleCode = (currentUser?.roleCode ?? "").toLowerCase();
  const isBusinessApprover = userRoleCode === "business_approver";
  const isBusinessReviewer = userRoleCode === "business_reviewer";
  const canAddSoftHold = hasPermission("CanAddSoftHold");
  const canViewSoftHold = hasPermission("CanViewSoftHold");
  const canViewAtlasRecommendation = hasPermission("CanViewAtlasRecommendation");

  function handleCreateSoftHoldFromRecommendation(item: RecommendationResponse) {
    const days = item.recommendationsByDateRangeResponses
      .map((r) => { const m = /^(\d{4}-\d{2}-\d{2})/.exec(r.day); return m ? m[1] : ""; })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const startDate = days[0] ?? "";
    const endDate = days.at(-1) ?? "";

    const prefilledOption: RoomBlockOption = {
      id: 1,
      startDate,
      endDate,
      meetingStartDate: startDate,
      meetingEndDate: endDate,
      estimatedAttendance: 0,
      isSelected: true,
      isItPrimaryDate: false,
      blockCountDayWise: days.map((day) => ({ day, blockCount: 0, isDeleted: false }))
    };

    setSoftHoldInitialRoomBlocks([prefilledOption]);
    setSelectedSoftHoldDetailsId(null);
    setShowCreateSoftHoldDialog(true);
  }

  // Data for the inline Draft Meeting create flyout
  const { data: fiscalYears = [] } = useFiscalYears();
  const activeFiscalYear = fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0];
  const { data: businessesData } = useDraftBusinesses();
  const draftBusinesses: Record<string, string[]> = businessesData ?? {};
  const { data: draftMeetingTypes = {} } = useDraftMeetingTypes();
  const { data: attendeeLevelsData } = useAttendeeLevels();
  const draftAttendeeLevels: string[] = attendeeLevelsData ?? [];
  const { data: locationsData } = useLocations();
  const draftLocations: string[] = locationsData ?? [];
  const { data: duDeclinedReasonsData } = useDUDeclinedReasons();
  const duDeclinedReasons: string[] = duDeclinedReasonsData ?? [];

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();
  const monthLabel = format(selectedDate, "MMM yyyy");

  const { data, isPending, isFetching, refetch } = useGRCData(month, year);

  useEffect(() => {
    if (!showAddNewMenu) return;

    function handlePointerDown(event: PointerEvent) {
      if (!addMenuRef.current) return;
      if (!addMenuRef.current.contains(event.target as Node)) {
        setShowAddNewMenu(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setShowAddNewMenu(false);
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [showAddNewMenu]);

  const draftCalendarItems = useMemo(() => {
    if (!data) return [];

    const seen = new Set<number>();
    return data.programDetails.filter((program) => {
      if (!program.isDraft || program.softHoldId <= 0) return false;
      if (seen.has(program.softHoldId)) return false;
      seen.add(program.softHoldId);
      return true;
    });
  }, [data]);

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goToPrevMonth = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }, []);

  const goToThisMonth = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // ── Search handlers ─────────────────────────────────────────────────────────

  function handleSearchSelect(result: GRCSearchResult) {
    // Navigate to the month of the result's start date and highlight
    const start = new Date(result.eventStartDate);
    setSelectedDate(new Date(start.getFullYear(), start.getMonth(), 1));
    setHighlightName(result.eventName);
    trackUserAction("GRC Dashboard", userId, `Searched for and selected event: ${result.eventName}`);
  }

  function handleSearchClear() {
    setHighlightName(undefined);
  }

  // ── Row interactions ────────────────────────────────────────────────────────

  function handleEventNameDoubleClick(prog: ProgramDetailDto) {
    if (prog.isSoftHold && prog.softHoldId > 0) {
      setShowSoftHoldsFlyout(false);
      setSelectedSoftHoldDetailsId(prog.softHoldId);
      setShowCreateSoftHoldDialog(true);
    } else if (prog.isDraft && prog.softHoldId > 0) {
      setDraftFlyoutViewOnly(true);
      setDraftFlyoutMeetingId(prog.softHoldId);
    }
  }

  function handleDraftEventClick(prog: ProgramDetailDto) {
    if (prog.isDraft && prog.softHoldId > 0) {
      setDraftFlyoutViewOnly(true);
      setLockedBy(null);
      setDraftFlyoutMeetingId(prog.softHoldId);
    }
  }

  function handleDraftItemFromFlyout(draftMeetingId: number) {
    if (draftMeetingId <= 0) return;
    setShowSoftHoldsFlyout(false);
    setDraftFlyoutViewOnly(true);
    setLockedBy(null);
    setDraftFlyoutMeetingId(draftMeetingId);
  }

  function handleDraftFlyoutSaved() {
    setDraftFlyoutMeetingId(null);
    setLockedBy(null);
    void refetch();
  }

  function handleDraftFlyoutClose() {
    setDraftFlyoutMeetingId(null);
    setLockedBy(null);
  }

  function handleOpenSoftHoldsList() {
    if (!data || data.totalSoftHoldAndDraftCount <= 0) return;
    setIsSoftHoldsListOnlyMode(true);
    setSelectedSoftHoldId(data?.programDetails.find((item) => item.isSoftHold && item.softHoldId > 0)?.softHoldId ?? 0);
    setShowSoftHoldsFlyout(true);
    trackUserAction("GRC Dashboard", userId, "Opened Soft Holds list");
  }

  function handleSoftHoldItemFromList(softHoldId: number, isDraftEvent?: boolean) {
    if (softHoldId <= 0) return;
    if (isDraftEvent) {
      setDraftFlyoutViewOnly(true);
      setDraftFlyoutMeetingId(softHoldId);
    } else {
      setShowSoftHoldsFlyout(false);
      setSelectedSoftHoldDetailsId(softHoldId);
      setShowCreateSoftHoldDialog(true);
    }
    trackUserAction("GRC Dashboard", userId, `Opened Soft Hold details - ID: ${softHoldId}`);
  }

  // ── Status bar content ──────────────────────────────────────────────────────

  const hasFilters = filters.eventTypes.length > 0 || filters.statuses.length > 0;

  return (
    <main className="flex flex-col gap-0" aria-label="GRC Dashboard">
      
      {/* ── Toolbar ── */}
      <div className="flex items-center border-b border-slate-200 bg-white px-3 py-2">
        {/* Left – month navigation */}
        <div className="flex flex-none items-center gap-2">
          <MonthNav
          
            label={monthLabel}
            onPrev={goToPrevMonth}
            monthLabelRef={monthLabelRef}
            onNext={goToNextMonth}
            onLabelClick={() => {
              if (monthLabelRef.current) {
                setTriggerRect(monthLabelRef.current.getBoundingClientRect());
              }
              setShowMonthPicker(true);
            }}
            showThisMonth
            onThisMonth={goToThisMonth}
           
          />
          <div className="mx-2 h-6 w-px bg-slate-200" aria-hidden="true" />
        </div>

        {/* Center – responsive search */}
        <div className="flex min-w-0 flex-1 justify-center px-2">
          <GRCSearchBox
            onSelect={handleSearchSelect}
            onClear={handleSearchClear}
            className="w-full max-w-sm"
          />
        </div>

        {/* Right – action buttons */}
        <div className="flex flex-none items-center gap-2">
          {/* Show Dates At Bottom toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
            <button
              type="button"
              role="switch"
              aria-checked={showDatesAtBottom}
              aria-label="Toggle show dates at bottom"
              onClick={() => setShowDatesAtBottom((v) => !v)}
              className={`relative h-5 w-9 rounded-full transition ${
                showDatesAtBottom ? "bg-[#26890d]" : "bg-slate-300"
              }`}
            >
              <span
                className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                style={{ left: showDatesAtBottom ? "18px" : "2px" }}
              />
            </button>
            Show Dates at Bottom
          </label>

          {/* Show Pickups toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
            <button
              type="button"
              role="switch"
              aria-checked={showPickups}
              aria-label="Toggle show pickups"
              onClick={() => setShowPickups((v) => !v)}
              className={`relative h-5 w-9 rounded-full transition ${
                showPickups ? "bg-[#26890d]" : "bg-slate-300"
              }`}
            >
              <span
                className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                style={{ left: showPickups ? "18px" : "2px" }}
              />
            </button>
            Show Pickups
          </label>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => refetch()}
            aria-label="Refresh data"
            className="flex h-8 w-8 items-center justify-center rounded-none border border-slate-300 text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>

          {/* Filter */}
          <button
            type="button"
            onClick={() => setShowFilterPanel(true)}
            aria-label="Open filters"
            className={`flex h-8 w-8 items-center justify-center rounded-none border ${
              hasFilters
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>

          {/* Export */}
          <button
            type="button"
            disabled={!canExport}
            onClick={() => setShowExportModal(true)}
            aria-label="Export GRC report"
            className="flex h-8 w-8 items-center justify-center rounded-none border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
          </button>

          {/* Atlas */}
          {canViewAtlasRecommendation && (
            <button
              ref={atlasButtonRef}
              type="button"
              onClick={() => setShowBeaconWindow(true)}
              aria-label="Open Atlas recommendations"
              className="flex h-8 w-8 items-center justify-center rounded-none border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="0.5" y="0.5" width="31" height="31" stroke="#D0D0CE" />
                <path d="M16.7692 11.068C17.0406 10.3396 17.7714 9.92911 18.5183 10.0101C19.2652 10.0911 19.8086 10.8181 20.2968 11.346C20.7416 11.4531 21.1697 11.5032 21.6058 11.6714C22.4914 12.0133 23.8977 13.2764 24.6033 13.9752C26.6558 16.0093 26.4117 19.4348 24.0978 21.1339C21.6418 22.938 18.0868 21.751 17.1879 18.7923C17.1559 18.6859 17.0379 18.0818 16.9892 18.0763C16.3464 18.4086 15.6182 18.3756 14.9713 18.0708C14.4732 21.3646 10.8109 23.0828 8.05884 21.2561C5.61885 19.6387 5.28876 16.0628 7.38466 13.9869C8.00349 13.3739 9.35652 12.1664 10.102 11.8073C10.5822 11.576 11.1016 11.4476 11.6285 11.3879C12.1526 10.9458 12.5734 10.2305 13.2696 10.0568C14.1185 9.84536 14.8793 10.2847 15.2581 11.0659C15.5548 10.9575 15.8002 10.8751 16.123 10.897C16.3644 10.9135 16.5424 11.0151 16.7692 11.068ZM10.3635 14.3418C8.22221 14.4599 6.92653 16.842 7.95214 18.7944C9.13579 21.0467 12.3847 20.8044 13.2536 18.4154C13.9858 16.4013 12.452 14.2272 10.3635 14.3425V14.3418ZM21.3991 14.3411C19.4826 14.4064 18.1362 16.3429 18.6957 18.2362C19.4172 20.6774 22.6121 21.1243 23.9537 18.9619C25.2148 16.9292 23.7224 14.2622 21.3991 14.3411ZM15.8302 15.0269C14.818 15.1793 14.918 16.82 15.9916 16.8331C17.2026 16.8482 17.1779 14.8237 15.8302 15.0269Z" fill="black" />
              </svg>
            </button>
          )}

          {/* Add New */}
          {canViewToggleSettings && (
            <div ref={addMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setShowAddNewMenu((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={showAddNewMenu}
                className="inline-flex items-center gap-1 rounded-none border border-[#26890d] bg-[#26890d] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1f6f0a]"
              >
                Add New
                <ChevronDown className="h-4 w-4" />
              </button>

              {showAddNewMenu && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-1 w-64 rounded-none border border-slate-200 bg-white py-1 shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canAddSoftHold}
                    onClick={() => {
                      setShowAddNewMenu(false);
                      setShowCreateSoftHoldDialog(true);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Soft Hold to GRC
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canAddMeeting}
                    onClick={() => {
                      setShowAddNewMenu(false);
                      setDraftFlyoutViewOnly(false);
                      setDraftFlyoutMeetingId(0);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Meeting to Draft Calendar
                  </button>
                </div>
              )}
            </div>
          )}

          {canViewSoftHold && (
            <button
              type="button"
              onClick={handleOpenSoftHoldsList}
              aria-label="Open soft holds list"
              disabled={!data || data.totalSoftHoldAndDraftCount <= 0}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-none border border-black bg-black text-sm font-normal leading-5 text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
            >
              {data?.totalSoftHoldAndDraftCount ?? 0}
            </button>
          )}

        </div>
      </div>

{/* ── Breadcrumbs ── */}
      <GRCBreadcrumbs breadcrumbs={breadcrumbs} onNavigate={handleBreadcrumbNavigate} />


      {/* ── Content ── */}
      <div className="flex-1 overflow-hidden px-3 pt-2">
        {isPending && !data && <GRCDashboardContentSkeleton />}

        {!isPending && (!data || data.programDetails.length === 0) && (
          <p className="py-10 text-center text-sm text-slate-500">
            No program data available for {monthLabel}.
          </p>
        )}

        {!isPending && data && data.programDetails.length > 0 && (
          <GRCGrid
            data={data}
            filters={filters}
            showPickups={showPickups}
            showDatesAtBottom={showDatesAtBottom}
            highlightEventName={highlightName}
            onEventNameDoubleClick={handleEventNameDoubleClick}
            onDraftEventClick={handleDraftEventClick}
          />
        )}
      </div>

            {/* ── Status legend bar ── */}
      {data && !isPending && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-1.5">
          {/* Left: color-coded event status swatches */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm bg-[#43B02A]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Definite</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm bg-[#FFCD00]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Tentative</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm bg-[#63666A]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Prospect</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm bg-[#62B5E5]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Soft Holds</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm border border-[#A7A8AA] bg-[#9368B7]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Core</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm border border-[#A7A8AA] bg-[#D1BCE3]" aria-hidden="true" />
              <span className="text-[10px] text-slate-700">Secondary</span>
            </span>
          </div>
          {/* Right: last updated + refresh */}
          {data.lastUpdateDisplay && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-black">
                Last updated:{" "}
                <span className="font-normal">{data.lastUpdateDisplay}</span>
              </span>
              <button
                type="button"
                onClick={() => void refetch()}
                aria-label="Refresh data"
                disabled={isFetching}
                className="flex h-7 w-7 items-center justify-center rounded-none border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Panels / Modals ── */}
      {showFilterPanel && (
        <GRCFilterPanel
          data={data}
          filters={filters}
          onFiltersChange={setFilters}
          onClose={() => setShowFilterPanel(false)}
        />
      )}

      {showExportModal && (
        <GRCExportModal
          month={month}
          year={year}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {showSoftHoldsFlyout && (
        <SoftHoldsFlyout
          month={month}
          year={year}
          initialSoftHoldId={selectedSoftHoldId}
          onSoftHoldItemClick={(softHoldId, isDraftEvent) => handleSoftHoldItemFromList(softHoldId, isDraftEvent)}
          onPreferredDateChanged={() => void refetch()}
          onClose={() => setShowSoftHoldsFlyout(false)}
        />
      )}

      {showCreateSoftHoldDialog && (
        <CreateSoftHoldDialog
          month={month}
          year={year}
          softHoldId={selectedSoftHoldDetailsId ?? undefined}
          initialRoomBlockOptions={softHoldInitialRoomBlocks}
          onCreated={() => {
            setSelectedSoftHoldId(0);
            setSelectedSoftHoldDetailsId(null);
          }}
          onResolved={(softHoldId, status) => {
            if (status === "Did Not Materialize") {
              setBreadcrumbs((prev) => prev.filter((b) => b.id !== softHoldId));
            }
          }}
          onClose={() => {
            setShowCreateSoftHoldDialog(false);
            setSelectedSoftHoldDetailsId(null);
            setSoftHoldInitialRoomBlocks(undefined);
          }}
        />
      )}

      <MonthYearPicker
        isOpen={showMonthPicker}
        currentDate={selectedDate}
        onDateSelect={(newDate) => setSelectedDate(newDate)}
        onClose={() => setShowMonthPicker(false)}
        triggerRect={triggerRect}
      />

      <BeaconWindow
        isOpen={showBeaconWindow}
        onClose={() => setShowBeaconWindow(false)}
        triggerRef={atlasButtonRef}
        isNlqEnabled={featureFlags.nlqChatEnabled}
        onCreateSoftHold={canAddSoftHold ? handleCreateSoftHoldFromRecommendation : undefined}
      />

      {draftFlyoutMeetingId !== null && (
        <MeetingFlyout
          meetingId={draftFlyoutMeetingId}
          fiscalYear={activeFiscalYear}
          businesses={draftBusinesses}
          meetingTypes={draftMeetingTypes}
          attendeeLevels={draftAttendeeLevels}
          locations={draftLocations}
          duDeclinedReasons={duDeclinedReasons}
          isViewOnly={draftFlyoutViewOnly}
          lockedBy={lockedBy}
          canEdit={canEditMeeting}
          canCopyToNew={canEditMeeting}
          canSubmit={canSubmit}
          isBusinessApprover={isBusinessApprover}
          isBusinessReviewer={isBusinessReviewer}
          canViewSignOffRequest={false}
          canSubmitSignOff={false}
          canEditSignOffStatus={false}
          signOffStatus={null}
          canPushToSummit={false}
          currentUserName={currentUser?.name ?? ""}
          currentUserEmail={userEmail}
          onEdit={() => setDraftFlyoutViewOnly(false)}
          onSaved={handleDraftFlyoutSaved}
          onClose={handleDraftFlyoutClose}
          onBack={() => setDraftFlyoutViewOnly(true)}
        />
      )}
    </main>
  );
}
